import type { RequiredDocumentTypeInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildRequiredDocumentTypeColumns } from './build-required-document-type-columns';
import type { RequiredDocumentType } from './reference-data.api';
import { RequiredDocumentTypeFormDrawer } from './RequiredDocumentTypeFormDrawer';

export interface RequiredDocumentTypesPanelProps {
  types: RequiredDocumentType[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: RequiredDocumentTypeInput) => Promise<boolean>;
  onUpdate: (id: string, input: RequiredDocumentTypeInput) => Promise<boolean>;
  onDeactivate: (row: RequiredDocumentType) => Promise<void>;
  onReactivate: (row: RequiredDocumentType) => Promise<void>;
  onResetSaveError: () => void;
}

/**
 * The shared vocabulary a job type's required documents are picked from —
 * its own compact table beneath the job types table, in the same tab rather
 * than a fifth top-level one, since it's managed alongside what uses it.
 */
export function RequiredDocumentTypesPanel({
  types,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: RequiredDocumentTypesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<RequiredDocumentType | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: RequiredDocumentType) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildRequiredDocumentTypeColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const newButton = (
    <Button size="small" icon={<Plus className="size-4" />} onClick={openCreate}>
      New required document
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-fg">Required documents</h3>
        {newButton}
      </div>
      {loading && <TableSkeleton label="Loading required documents" />}
      {!loading && types.length === 0 && (
        <EmptyState icon={FileText} title="No required documents yet" description="This catalogue ships empty — add the documents job types can require.">
          {newButton}
        </EmptyState>
      )}
      {!loading && types.length > 0 && (
        <Table rowKey="id" size="small" dataSource={types} columns={columns} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      )}
      <RequiredDocumentTypeFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
